import React, { FunctionComponent, useState } from "react";
import { HiOutlineChevronDown } from "react-icons/hi";

type TGuide = { title: string; steps: string[] };

/**
 * How to allow the camera and location (or the location alone), per browser. Plain steps: the wording differs too much between
 * browsers to share one.
 */
const guidesFor = (locationOnly: boolean): TGuide[] => {
  const what = locationOnly ? "Location" : "Camera and Location";
  return [
    {
      title: "Chrome or Edge",
      steps: [
        "Tap the lock or settings icon next to the address.",
        "Open Permissions (or Site settings).",
        `Set ${what} to Allow.`,
        "Come back here and try again."
      ]
    },
    {
      title: "Safari on iPhone or iPad",
      steps: [
        "Tap aA in the address bar, then Website settings.",
        `Set ${what} to Allow.`,
        "If Location stays off, open Settings, Privacy and Security, Location Services and turn it on for Safari."
      ]
    },
    {
      title: "Android phone",
      steps: ["Tap the lock icon, then Permissions.", `Allow ${what}.`, "Make sure the phone's Location switch is on."]
    },
    {
      title: "Safari or Firefox on a computer",
      steps: ["Open the browser's site settings for this page.", `Allow ${what}, then reload the page.`]
    }
  ];
};

/** The camera-and-location guides, as the Geo Capture form has always shown them. */
const GUIDES: TGuide[] = guidesFor(false);

const GuideItem: FunctionComponent<{ guide: TGuide; first: boolean }> = ({ guide, first }) => {
  const [open, setOpen] = useState(first);
  return (
    <div className={first ? "" : "border-0 border-t border-solid border-neutral-4"}>
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen(value => !value)}
        className="flex w-full cursor-pointer items-center justify-between gap-2 border-0 bg-transparent px-3 py-2.5 text-left text-sm font-medium text-neutral-12 focus:underline"
      >
        {guide.title}
        <HiOutlineChevronDown
          aria-hidden="true"
          className={`flex-shrink-0 text-base text-neutral-9 transform transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <ol className="m-0 flex flex-col gap-1 pb-3 pl-8 pr-3 text-xs leading-relaxed text-neutral-9">
          {guide.steps.map(step => (
            <li key={step}>{step}</li>
          ))}
        </ol>
      )}
    </div>
  );
};

/**
 * The "how to turn them on" accordion shared by the form's blocked notice, the camera dialog and the Week 0 office location
 * (`locationOnly`: its steps name the location alone). The first guide starts open.
 */
const PermissionHelpSteps: FunctionComponent<{ locationOnly?: boolean }> = ({ locationOnly = false }) => (
  <div className="w-full rounded-lg border border-solid border-neutral-4 bg-white">
    {(locationOnly ? guidesFor(true) : GUIDES).map((guide, index) => (
      <GuideItem key={guide.title} guide={guide} first={index === 0} />
    ))}
  </div>
);

export default PermissionHelpSteps;
